/**
 * Razorpay checkout helper — mutates the options object to surface only the
 * payment methods that actually settle in the order's currency BEFORE the
 * popup opens.
 *
 * Why this exists:
 *   Razorpay's domestic wallet / UPI / netbanking / paylater / EMI rails
 *   are INR-only. If we open the checkout with currency=USD and DON'T
 *   gate the methods, the popup surfaces all of them — and the moment a
 *   US visitor taps PayTM or Google Pay they get a fatal server-side
 *   error: `wallet_currency_validation: wallet_currency_not_supported`.
 *
 *   HOWEVER — Razorpay also classifies **PayPal** under `method:"wallet"`.
 *   PayPal IS USD-capable (it's literally Razorpay's recommended USD rail
 *   for Indian-registered merchants), so a blanket `wallet:false` blocks
 *   the one wallet that actually works internationally.
 *
 * What we do:
 *   For any non-INR order we build an explicit display-block whitelist
 *   that surfaces ONLY:
 *     • International debit/credit cards
 *     • PayPal (Razorpay's USD-capable wallet rail)
 *   …and hide everything else (Indian wallets, UPI, netbanking, paylater,
 *   EMI, cardless EMI). This matches Razorpay's documented pattern for
 *   accepting cross-border payments.
 *
 *   INR orders pass through untouched.
 *
 *   Returns the same options object so the call site can keep the
 *   one-liner `new window.Razorpay(applyCurrencyMethodGate(options))`.
 */
export function applyCurrencyMethodGate(options) {
  const cur = String(options?.currency || "INR").toUpperCase();
  if (cur === "INR") {
    // INR — explicitly enable EMI + Cardless-EMI + PayLater so they appear
    // in Razorpay's checkout when the order amount clears each provider's
    // per-bank minimum threshold (typically ₹3,000 for HDFC/ICICI/Kotak
    // card EMIs, ₹5,000 for BFL / Zest / LazyPay). Without an explicit
    // whitelist Razorpay sometimes hides these behind "other options"
    // even on eligible amounts. Order created with `partial_payment:false`
    // + currency:"INR" satisfies all other Razorpay EMI preconditions.
    options.method = options.method || {};
    if (options.method.emi === undefined) options.method.emi = true;
    if (options.method.cardless_emi === undefined) options.method.cardless_emi = true;
    if (options.method.paylater === undefined) options.method.paylater = true;
    if (options.method.upi === undefined) options.method.upi = true;
    if (options.method.netbanking === undefined) options.method.netbanking = true;
    if (options.method.card === undefined) options.method.card = true;
    if (options.method.wallet === undefined) options.method.wallet = true;

    // Force EMI to always render as a top-level payment row for eligible
    // amounts, EVEN for repeat customers where Razorpay's personalization
    // engine would normally hide it because they haven't used EMI before.
    //
    // Bug we hit: same order (₹14,640 gemstone) opened by two different
    // customers showed different method lists — a fresh phone number saw
    // EMI, but a repeat customer saw UPI/Cards/Netbanking/Wallet/PayLater
    // with EMI silently omitted. Reason: Razorpay's `personalization`
    // reorders/demotes methods based on the customer's history under this
    // key, and setting only `method.emi=true` on the client is not enough
    // to guarantee EMI surfaces on the personalized checkout view.
    //
    // Fix: register an explicit `config.display.blocks` entry that maps
    // EMI + Cardless-EMI into a named block, prepend it to the display
    // sequence, and keep `show_default_blocks:true` so the rest of the
    // personalized method list still renders below. This overrides
    // personalization for the EMI slot only — everything else keeps
    // Razorpay's smart ordering.
    //
    // Amount gate: only inject the forced EMI block when the order clears
    // ₹3,000 (the lowest per-bank EMI floor). Below that, Razorpay would
    // render the block and then show "No EMI options available" which is
    // worse UX than hiding it.
    const amountPaise = Number(options.amount || 0);
    if (amountPaise >= 300000) {
      options.config = options.config || {};
      options.config.display = options.config.display || {};
      options.config.display.blocks = options.config.display.blocks || {};
      options.config.display.blocks.emi_block = {
        name: "Pay via EMI",
        instruments: [
          { method: "emi" },
          { method: "cardless_emi" },
        ],
      };
      const existingSeq = Array.isArray(options.config.display.sequence)
        ? options.config.display.sequence
        : [];
      if (!existingSeq.includes("block.emi_block")) {
        options.config.display.sequence = ["block.emi_block", ...existingSeq];
      }
      options.config.display.preferences = options.config.display.preferences || {};
      if (options.config.display.preferences.show_default_blocks === undefined) {
        options.config.display.preferences.show_default_blocks = true;
      }
    }
    return options;
  }

  // Non-INR — surface only the rails that can actually settle this currency.
  //
  // Historical note: we used to hand-craft a `config.display.blocks.international`
  // whitelist with `instruments: [{method:"wallet", wallets:["paypal"]}, {method:"card"}]`
  // and set `method.wallet: true, method.paylater: false, …`. That approach
  // silently dropped the PayPal button on live traffic (bug reported Mar 2026
  // — Patricia's UK card was mis-classified "Domestic card", no PayPal row
  // rendered). Two reasons it failed:
  //
  //   1. Razorpay registers PayPal under BOTH `method.wallet.paypal` AND
  //      `method.paylater.paypal` on modern accounts. Setting
  //      `method.paylater: false` blocked the PayPal-cross-border rail
  //      that Razorpay's checkout picks for USD/GBP/EUR orders.
  //
  //   2. A hand-crafted display-block whitelist bypasses Razorpay's own
  //      per-currency personalization filter. Razorpay's checkout already
  //      knows Indian wallets (PayTM/GPay/Mobikwik/OlaMoney) aren't
  //      USD-capable — its default UI hides them automatically for non-INR
  //      orders and only surfaces PayPal + international cards. Overriding
  //      that with our own block does more harm than good.
  //
  // Current approach: enable card + wallet + paylater at the method level and
  // let Razorpay's default UI + preferences do the filtering. Explicitly
  // disable only upi + netbanking + emi (rails that are 100% INR-only).
  // Non-INR — surface ONLY PayPal + international cards via an EXPLICIT
  // display-block whitelist that bypasses Razorpay's default mobile UI
  // entirely. This is the exact config that worked reliably with our
  // previous LIVE Razorpay account — see git commit 47b8330. When we
  // deviated from it (removing the whitelist / setting show_default_blocks
  // to true) PayPal silently disappeared on iOS Safari because Razorpay's
  // mobile-default UI compression drops non-recommended wallets. Reverting
  // to the whitelist forces PayPal to always render regardless of device
  // personalization.
  //
  // The 4 rules that make this config work:
  //   1. `method.wallet: true` — PayPal lives inside Razorpay's wallet
  //      bucket, so we must allow the wallet rail. The display block
  //      below narrows the visible wallets to PayPal only.
  //   2. `method.paylater: false` — historically enabling paylater made
  //      Razorpay attempt to render lazy-loaded providers (Simpl / Zest /
  //      LazyPay) even on non-INR orders which caused UI flicker. Keep
  //      it off; PayPal reaches USD orders via the wallet rail on the
  //      account that works.
  //   3. Single `international` block with two instruments (wallet.paypal
  //      + card) and `show_default_blocks: false` — this REPLACES the
  //      full mobile UI with our two rows. No Apple Pay conflict, no
  //      personalization-based hiding, no accordion compression.
  //   4. `retry.enabled + timeout` — PayPal sub-merchant handshake can
  //      be slow; kept from the more recent iterations.
  options.method = {
    card: true,
    wallet: true,     // PayPal sits inside the wallet rail
    upi: false,       // INR-only rail
    netbanking: false,// INR-only rail
    paylater: false,  // INR-only paylater providers (Simpl / LazyPay / Zest)
    emi: false,       // INR-only rail
  };
  options.config = options.config || {};
  options.config.display = options.config.display || {};
  options.config.display.blocks = {
    international: {
      name: "Pay with PayPal, Google Pay or card",
      instruments: [
        // Google Pay International — Razorpay experiment
        // `is_google_pay_international_enabled: variant_on` is active on
        // this merchant account, so GPay renders a big Pay button on
        // Android Chrome + Chromium Edge for USD orders.
        { method: "wallet", wallets: ["googlepay"] },
        // PayPal Wallet — the primary international rail on desktop.
        { method: "wallet", wallets: ["paypal"] },
        // International Cards — VISA/MC/Amex/JCB/DINERS via Razorpay's
        // enhanced flow (experiment `international_card_flow_enhance: on`).
        { method: "card" },
      ],
    },
  };
  options.config.display.sequence = ["block.international"];
  options.config.display.preferences = { show_default_blocks: false };
  // Razorpay's PayPal sub-merchant handshake sometimes fails on the very
  // first session-create call ("Payment could not be completed. Please
  // try again.") and reliably succeeds on the second attempt — users see
  // a confusing one-shot error and have to re-click PayPal. Enabling the
  // built-in auto-retry makes that handshake-warmup invisible: Razorpay
  // silently retries up to 4 times before surfacing any error to the user.
  options.retry = { enabled: true, max_count: 4 };
  // Give PayPal's session-init + sub-merchant negotiation more headroom
  // than the default 300s — international rails are slower than UPI.
  options.timeout = 900;
  return options;
}

/**
 * Safer factory — wraps `new window.Razorpay(...)` with a fallback path
 * for non-INR currencies. If the PayPal+card display-block config throws
 * inside Razorpay checkout.js (which has been observed on accounts where
 * PayPal sub-merchant onboarding is incomplete), we retry with a barebones
 * `method: {card:true,…rest:false}` config. This guarantees US visitors
 * can still pay by card even if PayPal isn't fully provisioned.
 *
 * Use this in place of `new window.Razorpay(...)` at call sites.
 */
export function createRazorpayCheckout(options) {
  const gated = applyCurrencyMethodGate(options);
  try {
    return new window.Razorpay(gated);
  } catch (err) {
    if (gated.config) {
      // eslint-disable-next-line no-console
      console.warn(
        "[razorpay] custom display-block config threw — falling back to card-only:",
        err,
      );
      const fallback = {
        ...gated,
        method: {
          card: true,
          wallet: false,
          upi: false,
          netbanking: false,
          paylater: false,
          emi: false,
        },
        config: undefined,
      };
      return new window.Razorpay(fallback);
    }
    throw err;
  }
}
